import React, { useEffect, useState } from 'react';
import {
    Box,
    Slider,
    Typography,
    Stack,
} from '@mui/material';
import { AppConfigs, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';

const sliderLeftLabel = 'Less important';
const sliderRightLabel = 'More important';

const CustomSelectSetting2 = ({
    id,
    items,
    onTradeoffValueChange,
    setCriteriaConstraints,
    criteriaConstraints,
    disabled = false,
    highlightedOptionNames = [],
}: {
    disabled?: boolean;
    importance: number;
    items: {
        name: string;
        value: number | string;
        icon: string;
        link: string;
    }[];
    hideValues?: boolean;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange']
    initialValues: Record<string, string>;
    id: string;
    criteriaConstraints: Record<string, CriteriaConstraint[]>;
    setCriteriaConstraints: (value: (((prevState: { [p: string]: CriteriaConstraint[]; }) => {
        [p: string]: CriteriaConstraint[];
    }) | { [p: string]: CriteriaConstraint[]; })) => void;
    variant?: AppConfigs['criteriaSettings']['enumerated'];
    isModal?: boolean;
    onCriteriaSettingsOpen?: (id: string) => void;
    highlightedOptionNames?: string[];
}) => {
    const [ itemValues, setItemValues ] = useState<{
        [key: string]: {
            isChecked: boolean;
            value: string;
        }
    }>({});

    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                {
                    ...criteriaConstraints,
                    [id]: [],
                }
            ));
        }

        const itemsChecked: { [key: string]: { isChecked: boolean; value: string; } } = {};
        items.forEach(item => {
            itemsChecked[item.name] = {
                isChecked: item.value !== '-1',
                value: item.value as string,
            };
        });
        setItemValues(itemsChecked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Stack spacing={4}>
            <Stack spacing={1.5}>
                {items.map((item, idx) => (
                    <Box sx={{ className: highlightedOptionNames.includes(item.name) ? 'option-highlighted' : '' }}>
                        <Typography
                            sx={{
                                fontSize: { xs: 14, md: 18 },
                                color: '#1e2f46',
                            }}
                        >
                            {item.name}
                        </Typography>
                        <Box sx={{ px: { xs: 0, md: 1 } }}>
                            <Slider
                                defaultValue={100 - 20 * (idx + 1)}
                                min={0}
                                max={100}
                                step={1}
                                disabled={!itemValues[item.name]?.isChecked || disabled}
                                value={+itemValues[item.name]?.value}
                                onChange={(_, value) => {
                                    setItemValues(items => (
                                        {
                                            ...items,
                                            [item.name]: {
                                                ...items[item.name],
                                                value: value + '',
                                            },
                                        }
                                    ));
                                }}
                                onChangeCommitted={(_, value) => onTradeoffValueChange(value, id, item.name)}
                                sx={{
                                    height: 10,
                                    color: 'var(--app-primary, #2878d4)',
                                    '& .MuiSlider-rail': {
                                        opacity: 1,
                                        backgroundColor: 'var(--app-secondary, #ffffff)',
                                        height: 10,
                                        borderRadius: 999,
                                    },
                                    '& .MuiSlider-track': {
                                        border: 'none',
                                        height: 10,
                                        borderRadius: 999,
                                        backgroundColor: 'var(--app-primary, #2878d4)',
                                    },
                                    '& .MuiSlider-thumb': {
                                        width: 24,
                                        height: 24,
                                        backgroundColor: 'var(--app-secondary, #ffffff)',
                                        border: '4px solid var(--app-primary, #2878d4)',
                                        boxShadow: 'none',
                                        '&:hover, &.Mui-focusVisible, &.Mui-active': {
                                            boxShadow: 'none',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Stack>

            <Box sx={{ pt: 1 }}>
                <Box
                    sx={{
                        mt: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 14, md: 18 },
                            color: '#667892',
                            fontWeight: 500,
                        }}
                    >
                        {sliderLeftLabel}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: 14, md: 18 },
                            color: '#667892',
                            fontWeight: 500,
                            textAlign: 'right',
                        }}
                    >
                        {sliderRightLabel}
                    </Typography>
                </Box>
            </Box>
        </Stack>

    );
};

export default CustomSelectSetting2;
